import { apiFetch } from "@/lib/api-client";
import type {
  BankAccountDto,
  BankAccountFieldsDto,
  BankingPageDto,
  BankingBatchSummaryDto,
  BankingBatchDetailDto,
  BankingDuplicateDecisionDto,
  BankingCompactDecisionDto,
  BankingConfirmationDto,
  BankMovementDto,
} from "./types";

const root = "/api/banking";
const id = encodeURIComponent;
export function bankingQuery(values: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values))
    if (value) query.set(key, value);
  return `?${query}`;
}

/** Each method mirrors the Banking HTTP Contract; no domain validation occurs here. */
export const bankingApi = {
  accounts: (cursor?: string, signal?: AbortSignal) =>
    apiFetch<BankingPageDto<BankAccountDto>>(
      `${root}/accounts${bankingQuery({ limit: "50", cursor })}`,
      { signal },
    ),
  saveAccount: (
    fields: BankAccountFieldsDto,
    account: BankAccountDto | null,
    signal: AbortSignal,
  ) =>
    apiFetch<BankAccountDto>(
      `${root}/accounts${account ? `/${id(account.id)}` : ""}`,
      {
        method: account ? "PATCH" : "POST",
        signal,
        body: account
          ? { expectedRevision: account.revision, changes: fields }
          : fields,
      },
    ),
  instructions: (signal: AbortSignal) =>
    apiFetch<{ instructions: string }>(`${root}/templates/instructions`, {
      signal,
    }),
  template: (
    format: "csv" | "xlsx",
    delimiter: "," | ";",
    signal: AbortSignal,
  ) =>
    apiFetch.blob(
      `${root}/templates/${format}${format === "csv" ? bankingQuery({ delimiter }) : ""}`,
      { signal },
    ),
  preview: (accountId: string, file: File, signal: AbortSignal) => {
    const body = new FormData();
    body.append("file", file);
    return apiFetch<BankingBatchDetailDto>(
      `${root}/accounts/${id(accountId)}/imports`,
      { method: "POST", body, signal },
    );
  },
  history: (accountId?: string, cursor?: string, signal?: AbortSignal) =>
    apiFetch<BankingPageDto<BankingBatchSummaryDto>>(
      `${root}/imports${bankingQuery({ bankAccountId: accountId, limit: "25", cursor })}`,
      { signal },
    ),
  detail: (batchId: string, signal: AbortSignal) =>
    apiFetch<BankingBatchDetailDto>(`${root}/imports/${id(batchId)}`, {
      signal,
    }),
  refresh: (batch: BankingBatchDetailDto, signal: AbortSignal) =>
    apiFetch<BankingBatchDetailDto>(`${root}/imports/${id(batch.id)}/refresh`, {
      method: "POST",
      signal,
      body: { expectedPreviewRevision: batch.previewRevision },
    }),
  decide: async (
    batch: BankingBatchDetailDto,
    decisions: BankingDuplicateDecisionDto[],
    signal: AbortSignal,
  ) => {
    const compact: BankingCompactDecisionDto[] = [];
    for (const decision of decisions) {
      signal.throwIfAborted();
      const bytes = new TextEncoder().encode(
        JSON.stringify([
          1,
          batch.id,
          batch.previewRevision,
          decision.sourceRow,
          [...decision.candidateIds].sort(),
        ]),
      );
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      compact.push([
        decision.sourceRow,
        decision.action,
        Array.from(new Uint8Array(digest), (byte) =>
          byte.toString(16).padStart(2, "0"),
        ).join(""),
      ]);
    }
    signal.throwIfAborted();
    return apiFetch<BankingBatchDetailDto>(
      `${root}/imports/${id(batch.id)}/decisions`,
      {
        method: "POST",
        signal,
        body: {
          expectedPreviewRevision: batch.previewRevision,
          decisions: compact,
        },
      },
    );
  },
  confirm: (batch: BankingBatchDetailDto, signal: AbortSignal) =>
    apiFetch<BankingConfirmationDto>(
      `${root}/imports/${id(batch.id)}/confirm`,
      {
        method: "POST",
        signal,
        body: { expectedPreviewRevision: batch.previewRevision },
      },
    ),
  movements: (
    accountId: string,
    startDate: string,
    endDate: string,
    cursor?: string,
    signal?: AbortSignal,
  ) =>
    apiFetch<BankingPageDto<BankMovementDto>>(
      `${root}/movements${bankingQuery({ bankAccountId: accountId, startDate, endDate, limit: "25", cursor })}`,
      { signal },
    ),
};
